import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { currentAppLanguage } from "../../utils/appLanguages";
import { localizedExerciseName } from "../../utils/localizedCatalogFields";
import TimerIcon from "@mui/icons-material/Timer";
import {
  TE_STATE_PAUSED,
  TE_STATE_STARTED,
} from "../../utils/coachExerciseNavigation";
import { useKotRoundElapsedSeconds } from "../../hooks/useKotHeroTimers";
import { normalizeKotMode } from "../../utils/kotCoachSubmit";
import { LiveTrainingClubLogo } from "./LiveTrainingClubLogo";
import "./LiveTrainingKotTitleBar.css";

function formatTimer(seconds) {
  if (seconds == null || Number.isNaN(Number(seconds))) return "--:--";
  const s = Math.max(0, Math.floor(Number(seconds)));
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, "0")}`;
}

function getExerciseName(exercise, lang, t) {
  if (!exercise) return t("exercise");
  return localizedExerciseName(exercise, lang) || t("exercise");
}

/**
 * Compact KoT title bar for player live training arena.
 */
export function LiveTrainingKotTitleBar({
  user = null,
  exercise = null,
  exerciseNumber = null,
  exerciseStateId,
  kotSnapshot = null,
  tableNumber = null,
  timerSeconds: timerSecondsProp = null,
  className = "",
}) {
  const { t, i18n } = useTranslation();
  const lang = currentAppLanguage(i18n);

  const isActive =
    exerciseStateId === TE_STATE_STARTED || exerciseStateId === TE_STATE_PAUSED;
  const isPaused = exerciseStateId === TE_STATE_PAUSED;
  const roundState = kotSnapshot?.current_round?.round_state ?? null;
  const roundActive =
    roundState === "IN_PROGRESS" || roundState === "STOP_REQUESTED";

  const roundElapsedHook = useKotRoundElapsedSeconds(kotSnapshot);
  const [liveRoundElapsed, setLiveRoundElapsed] = useState(null);

  useEffect(() => {
    if (timerSecondsProp != null) {
      setLiveRoundElapsed(timerSecondsProp);
      return undefined;
    }

    const base = kotSnapshot?.round_timer?.round_elapsed_seconds;
    if (base == null || !roundActive) {
      setLiveRoundElapsed(base != null ? Number(base) : roundElapsedHook);
      return undefined;
    }

    const anchorMs = Date.now() - Number(base) * 1000;
    const tick = () => {
      setLiveRoundElapsed(Math.max(0, Math.floor((Date.now() - anchorMs) / 1000)));
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [
    timerSecondsProp,
    kotSnapshot?.round_timer?.round_elapsed_seconds,
    roundActive,
    roundElapsedHook,
  ]);

  const timerSeconds =
    timerSecondsProp != null
      ? timerSecondsProp
      : isActive && roundActive
        ? liveRoundElapsed
        : liveRoundElapsed;

  const config = kotSnapshot?.config || {};
  const kotMode = normalizeKotMode(config.match_winner_mode || config.kot_mode);
  const setTarget = Math.max(3, Number(config.set_points_target) || 11);
  const roundNumber = kotSnapshot?.current_round?.round_number;
  const exerciseName = getExerciseName(exercise, lang, t);

  const params = useMemo(() => {
    const items = [
      {
        key: "mode",
        label: t("kingOfTheTable"),
        variant: "mode",
      },
    ];

    if (roundNumber != null) {
      items.push({
        key: "round",
        label: `${t("kotPlayerMatchRound")} ${roundNumber}`,
        variant: "round",
      });
    }

    if (tableNumber != null) {
      items.push({
        key: "table",
        label: t("table_with_number", { number: tableNumber }),
        variant: "table",
      });
    }

    const ruleText =
      t("kotPlayerArenaSetTo", { n: setTarget }) || `First to ${setTarget}`;
    const winBy =
      kotMode === "FULL_SET" && config.set_win_by
        ? ` · ${t("kotSetWinBy")} ${config.set_win_by}`
        : "";
    items.push({
      key: "rule",
      label: `${ruleText}${winBy}`,
      variant: "rule",
    });

    return items;
  }, [t, roundNumber, tableNumber, setTarget, kotMode, config.set_win_by]);

  const timerClass = [
    "lt-kot-bar__timer",
    isPaused ? " lt-kot-bar__timer--paused" : "",
    !isActive ? " lt-kot-bar__timer--idle" : "",
  ]
    .filter(Boolean)
    .join("");

  return (
    <header
      className={`lt-kot-bar${className ? ` ${className}` : ""}`}
      aria-label={exerciseName}
    >
      <div className="lt-kot-bar__logo">
        <LiveTrainingClubLogo user={user} size="bar" className="lt-kot-bar__club-logo" />
      </div>
      <div className="lt-kot-bar__content">
        <div className="lt-kot-bar__title-col">
          <span className="lt-kot-bar__title-line">
            {exerciseNumber != null ? (
              <span className="lt-kot-bar__number">{exerciseNumber}.</span>
            ) : null}
            <span className="lt-kot-bar__name">{exerciseName}</span>
          </span>
        </div>
        <div className="lt-kot-bar__meta-row">
          <div className="lt-kot-bar__params" aria-label={t("parameters") || "Parameters"}>
            {params.map((p) => (
              <span
                key={p.key}
                className={`lt-kot-bar__pill lt-kot-bar__pill--${p.variant}`}
              >
                {p.label}
              </span>
            ))}
          </div>
          <span className={timerClass} aria-live="polite">
            <TimerIcon fontSize="inherit" aria-hidden />
            {formatTimer(timerSeconds)}
          </span>
        </div>
      </div>
    </header>
  );
}

export default LiveTrainingKotTitleBar;
